import {
  AssetApiClient,
  createAssetApiClient,
} from '@peritasai/data-access';
import {
  AssetFilterParams,
  AssetHistoryQueryParams,
  PaginatedAssetHistoryResponseDto,
  AssetResponseDto,
  PaginatedAssetResponseDto,
  CreateAssetDto,
  UpdateAssetDto,
} from '@peritasai/types';
import { AssetEntity } from './asset.entity';

export interface IAssetRepository {
  getAssets(options: AssetFilterParams): Promise<AssetEntity[]>;
  getAssetsPaginated(
    options: AssetFilterParams
  ): Promise<PaginatedAssetResponseDto & { assets: AssetEntity[] }>;
  getAssetById(id: string): Promise<AssetEntity>;
  getAssetHistory(
    assetId: string,
    params: AssetHistoryQueryParams
  ): Promise<PaginatedAssetHistoryResponseDto>;
  createAsset(dto: CreateAssetDto): Promise<AssetEntity>;
  updateAsset(id: string, dto: UpdateAssetDto): Promise<AssetEntity>;
}

export class AssetRepository implements IAssetRepository {
  constructor(private readonly assetApiClient: AssetApiClient) {}

  async getAssets(options: AssetFilterParams = {}): Promise<AssetEntity[]> {
    const paginatedResponse = await this.getAssetsPaginated(options);
    return paginatedResponse.assets;
  }

  async getAssetById(id: string): Promise<AssetEntity> {
    const dto = await this.assetApiClient.getAssetById(id);
    return this.toEntity(dto);
  }

  async getAssetsPaginated(
    options: AssetFilterParams = {}
  ): Promise<PaginatedAssetResponseDto & { assets: AssetEntity[] }> {
    const assetsResponse: PaginatedAssetResponseDto =
      await this.assetApiClient.getAssets(options);
    const assetEntities = assetsResponse.data.map((dto) => this.toEntity(dto));
    return {
      ...assetsResponse,
      assets: assetEntities,
    };
  }

  async getAssetHistory(
    assetId: string,
    params: AssetHistoryQueryParams
  ): Promise<PaginatedAssetHistoryResponseDto> {
    return this.assetApiClient.getAssetHistory(assetId, params);
  }

  async createAsset(dto: CreateAssetDto): Promise<AssetEntity> {
    const result = await this.assetApiClient.createAsset(dto);
    return this.toEntity(result);
  }

  async updateAsset(id: string, dto: UpdateAssetDto): Promise<AssetEntity> {
    const result = await this.assetApiClient.updateAsset(id, dto);
    return this.toEntity(result);
  }

  private toEntity(dto: AssetResponseDto): AssetEntity {
    return new AssetEntity(dto);
  }
}

const defaultAssetApiClient = createAssetApiClient();

export const assetRepository = new AssetRepository(defaultAssetApiClient);
